/**
 * Dish Search Results Store
 * Event-driven store for managing dish search state
 */

import { EventDrivenStore } from '@core/store/eventDrivenStore';
import { EVENTS, DEFAULT_STATE } from './constants';

class DishSearchStore extends EventDrivenStore {
  constructor() {
    super('dishSearch', DEFAULT_STATE);
    this.setupEventListeners();
  }

  setupEventListeners() {
    console.log('[DishSearchStore] Setting up event listeners');

    // Listen for new search requests (resets state)
    this.on(EVENTS.DISH_SEARCH.REQUEST, (payload) => {
      console.log('[DishSearchStore] REQUEST event received:', payload);
      this.handleNewSearch(payload);
    });

    // Listen for loading state
    this.on(EVENTS.DISH_SEARCH.LOADING, () => {
      this.updateState({ loading: true, error: null });
    });

    // Listen for successful search results
    this.on(EVENTS.DISH_SEARCH.LOADED, (data) => {
      console.log('[DishSearchStore] LOADED event received:', data);
      this.handleSearchLoaded(data);
    });

    // Listen for errors
    this.on(EVENTS.DISH_SEARCH.ERROR, (error) => {
      console.error('[DishSearchStore] ERROR event received:', error);
      this.updateState({
        loading: false,
        error: error.message || 'An error occurred while searching dishes',
        hasMore: false,
      });
    });

    // Listen for load more requests
    this.on(EVENTS.DISH_SEARCH.LOAD_MORE, () => {
      console.log('[DishSearchStore] LOAD_MORE event received');
      this.handleLoadMore();
    });

    // Listen for loading more state
    this.on(EVENTS.DISH_SEARCH.LOADING_MORE, () => {
      this.updateState({ loading: true });
    });

    // Listen for more results loaded
    this.on(EVENTS.DISH_SEARCH.LOADED_MORE, (data) => {
      console.log('[DishSearchStore] LOADED_MORE event received:', data);
      this.handleMoreLoaded(data);
    });

    // Listen for reset
    this.on(EVENTS.DISH_SEARCH.RESET, () => {
      console.log('[DishSearchStore] RESET event received');
      this.resetState();
    });

    console.log('[DishSearchStore] Event listeners set up successfully');
  }

  /**
   * Handle new search request - reset state and set initial data
   */
  handleNewSearch(payload) {
    console.log('[DishSearchStore] Handling new search:', payload);
    this.updateState({
      ...DEFAULT_STATE,
      searchQuery: payload.searchQuery || '',
      filters: payload.filters || {},
      loading: true,
      error: null,
    });
  }

  /**
   * Handle search results loaded - replace all dishes
   */
  handleSearchLoaded(data) {
    console.log('[DishSearchStore] Handling search loaded:', data);
    this.updateState({
      dishes: data.dishes || [],
      loading: false,
      error: null,
      hasMore: data.hasMore ?? true,
      page: data.page || 1,
      totalCount: data.totalCount || 0,
    });
  }

  /**
   * Handle load more request - just set loading state
   */
  handleLoadMore() {
    const currentState = this.state;
    if (currentState.hasMore && !currentState.loading) {
      console.log('[DishSearchStore] Handling load more');
      // Don't increment page here - controller will use page + 1 for the request
      // Page will be updated when LOADED_MORE event is received
      this.updateState({
        loading: true,
      });
    }
  }

  /**
   * Handle more results loaded - accumulate dishes
   */
  handleMoreLoaded(data) {
    console.log('[DishSearchStore] Handling more loaded:', data);
    const currentState = this.state;
    this.updateState({
      dishes: [...currentState.dishes, ...(data.dishes || [])],
      loading: false,
      error: null,
      hasMore: data.hasMore ?? false,
      totalCount: data.totalCount || currentState.totalCount,
      page: data.page || currentState.page + 1, // Update page from event or increment
    });
  }
}

// Singleton instance
export default DishSearchStore;
